/* eslint-disable react/jsx-props-no-spreading */ // react-dropzone
import React, { useState, useEffect, useCallback } from 'react';
import { translate } from 'translations/i18n';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import cx from 'classnames';

import { Button, ProgressBar, Text } from '@companycam/slab-web';

import { IMAGE_MAX_SIZE_BYTES } from 'constants/files';

// #region styled-components
const TaskListDropZone = styled.div`
  max-width: 100%;
  margin: var(--cc_size_spacing_l) 0 0;
  padding: var(--cc_size_spacing_xxl);
  text-align: center;
  border: var(--cc_size_border_width_m) dashed
    var(--cc_color_border_input_inactive);
  border-radius: var(--cc_size_border_radius_l);
  cursor: pointer;
  transition: all ease-in-out 250ms;

  &:hover {
    border: var(--cc_size_border_width_m) solid
      var(--cc_color_border_input_active);
  }
`;

const ErrorMessageList = styled.ul`
  margin: var(--cc_size_spacing_m) 0;
`;

const ErrorMessage = styled.li`
  margin: var(--cc_size_spacing_s) 0;
  color: var(--cc_color_utility_destroy);
  font-size: var(--cc_size_text_s);
  line-height: 1.25;
`;
// #endregion

const DragAndDropUploader = ({
  uploader,
  maxFileSizeBytes = IMAGE_MAX_SIZE_BYTES,
  onFinishedCallback = () => {},
  totalUploadedFiles = 0,
  maxReferencePhotoUploadCount = Infinity,
}) => {
  const [files, setFiles] = useState([]);
  const [finished, setFinished] = useState(0);
  const [errorAlert, setErrorAlert] = useState([]);
  const [uploading, setUploading] = useState(false);

  const onDrop = async (acceptedFiles, rejectedFiles) => {
    setErrorAlert([]);
    const formatErrorMessageDropzoneRejection = ({ file }) => {
      if (file.size > maxFileSizeBytes) {
        return translate(
          'projects.show.photoUploadModal.errorMessageTooLarge',
          {
            fileName: file.name,
          },
        );
      }
      return translate(
        'projects.show.photoUploadModal.errorMessageWrongFormat',
        {
          fileName: file.name,
        },
      );
    };

    const uploadable = [...files, ...acceptedFiles];
    const lineSize = uploadable.length;
    const maxAllowed = maxReferencePhotoUploadCount - totalUploadedFiles;

    const allRejectedFiles = rejectedFiles.map(
      formatErrorMessageDropzoneRejection,
    );

    if (lineSize > maxAllowed) {
      allRejectedFiles.unshift(
        translate('projects.show.photoUploadModal.errorMessageExceedLimit'),
      );
    }
    if (allRejectedFiles.length) {
      setErrorAlert([...allRejectedFiles]);
      return;
    }

    if (finished > uploadable.length) {
      setFinished(0);
    }

    setFiles(uploadable);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    multiple: true,
    maxSize: maxFileSizeBytes,
    onDrop,
  });

  const uploadPhoto = useCallback(
    async (file) => {
      setUploading(true);
      const success = await uploader(file);
      if (success) setFinished((f) => f + 1);
      else files.splice(finished, 1);
      setUploading(false);
    },
    [files, finished, uploader],
  );

  const cancelPendingUploads = () => {
    setFinished(0);
    setFiles([]);
  };

  useEffect(() => {
    if (finished && files.length === finished) {
      onFinishedCallback(files);
      setFiles([]);
    }
  }, [finished, files, onFinishedCallback, errorAlert]);

  useEffect(() => {
    if (files.length && finished < files.length && !uploading) {
      uploadPhoto(files[finished]);
    }
  }, [files, finished, uploadPhoto, uploading]);

  useEffect(() => {
    return cancelPendingUploads();
  }, []);

  const renderStatus = () => {
    if (finished > files.length) {
      return (
        <Text size="m" color="subtle" data-testid="dd-uploader-instructions">
          {translate('projects.taskList.taskPhotoUploadModal.photosFinished')}
        </Text>
      );
    }
    if (files.length === 0) {
      return (
        <Text size="m" color="subtle" data-testid="dd-uploader-instructions">
          {translate('projects.show.photoUploadModal.modalInstructions')}
        </Text>
      );
    }

    const uploadProgress = Math.round(100 * (finished / files.length));

    return (
      <>
        <ProgressBar
          id="dnd-upload-progress"
          max={100}
          value={uploadProgress}
          label={translate('common.percentComplete')}
          size="medium"
          hideLabel
        />
        <Text ccMargin="s 0 0">
          {translate('projects.show.photoUploadModal.uploading', {
            count: files.length,
          })}
        </Text>
      </>
    );
  };

  const taskListDropzoneClasses = cx('task-list-drop-zone', {
    'task-list-drop-zone-active': isDragActive,
  });

  return (
    <>
      <TaskListDropZone
        {...getRootProps()}
        name="task-list-drop-zone"
        className={taskListDropzoneClasses}
      >
        <input {...getInputProps()} />
        {renderStatus()}
      </TaskListDropZone>
      {errorAlert.length > 0 ? (
        <>
          <ErrorMessageList>
            {errorAlert.map((err) => (
              <ErrorMessage key={err}>{err}</ErrorMessage>
            ))}
          </ErrorMessageList>
          <Button
            size="small"
            onClick={(e) => {
              e.preventDefault();
              setErrorAlert([]);
            }}
          >
            {translate('projects.show.photoUploadModal.dismissErrors')}
          </Button>
        </>
      ) : null}
    </>
  );
};

DragAndDropUploader.propTypes = {
  maxFileSizeBytes: PropTypes.number,
  onFinishedCallback: PropTypes.func,
  uploader: PropTypes.func.isRequired,
  totalUploadedFiles: PropTypes.number,
  maxReferencePhotoUploadCount: PropTypes.number,
};

export default DragAndDropUploader;
